/**
 * Fetching a typeface, when a report actually asks for one.
 *
 * Loaded on demand rather than all at once. Five typefaces bundled into the
 * entry would be five downloads on every page load to satisfy the one report
 * in twenty that is not set in the default, which is a cost paid by everybody
 * for the benefit of almost nobody.
 *
 * The imports below are written out one per case rather than built from the
 * id, because a bundler can only split what it can see. `import(path)` with a
 * computed path either fails or pulls in everything it might have meant.
 */

import { DEFAULT_TYPEFACE, libraryFamilyId, type TypefaceId } from '@/domain/typeface'
import { drawLibraryTypeface } from '@/features/typography/libraryFonts'

/** Typefaces already fetched, so a report reopened does not fetch again. */
const loaded = new Set<TypefaceId>()

/** Fetches in flight, so switching back and forth does not start two. */
const loading = new Map<TypefaceId, Promise<void>>()

async function fetchTypeface(id: TypefaceId): Promise<void> {
  // A family of the font library: fetched from our API, file by file.
  if (libraryFamilyId(id) !== null) return drawLibraryTypeface(id)
  switch (id) {
    case 'inter':
      // Already in the entry: the interface itself is set in it.
      return
    case 'source-serif':
      await import('@fontsource-variable/source-serif-4')
      return
    case 'lora':
      await import('@fontsource-variable/lora')
      return
    case 'plex-sans':
      await import('@fontsource-variable/ibm-plex-sans')
      return
    case 'garamond':
      await import('@fontsource-variable/eb-garamond')
      return
  }
}

/**
 * Make sure a typeface is available.
 *
 * Resolves when the stylesheet is in place; the browser then fetches the file
 * itself and swaps it in. Nothing waits on that, because the fallback stack
 * renders immediately and a page held blank until a font arrives is worse than
 * one that reflows.
 *
 * A failure is swallowed. The report is still perfectly readable in the
 * fallback, and stopping somebody from opening their own work because a font
 * did not download would be absurd.
 */
export async function ensureTypeface(id: TypefaceId): Promise<void> {
  if (loaded.has(id)) return

  const already = loading.get(id)
  if (already) return already

  const work = fetchTypeface(id)
    .then(() => {
      loaded.add(id)
    })
    .catch((error: unknown) => {
      console.warn(`[typography] ${id} could not be loaded; using the fallback`, error)
    })
    .finally(() => {
      loading.delete(id)
    })

  loading.set(id, work)
  return work
}

/**
 * Fetch every typeface, for the chooser.
 *
 * The one place loading them all is right: somebody looking at five samples
 * needs to see five typefaces, and showing five identical fallback stacks
 * would make the choice meaningless. Called when the chooser opens, not
 * before.
 */
export async function ensureAllTypefaces(ids: readonly TypefaceId[]): Promise<void> {
  await Promise.all(ids.map(ensureTypeface))
}

/** The headings' typeface, where a charter names one of its own. */
export async function ensureHeadingFace(id: TypefaceId | 'document'): Promise<void> {
  if (id !== 'document') await ensureTypeface(id)
}

/** Whether a typeface has been fetched. Used by the export, which needs the file. */
export function isLoaded(id: TypefaceId): boolean {
  return id === DEFAULT_TYPEFACE || loaded.has(id)
}
